/* ─────────────────────────────────────────────────────────────
   What the access screens read off the shared records.

   The store keeps only what happened to a door: its alarm status, a
   desk override, a pulse end time. What the door *is* right now
   (locked, unlocked, held open) is worked out here, from those plus the
   lockdown and the schedules, so the staff grid and the resident tiles
   can never disagree about it.
   ───────────────────────────────────────────────────────────── */

export const STATES = {
    locked:   { label: 'Locked',    tone: 'ok' },
    unlocked: { label: 'Unlocked',  tone: 'info' },
    open:     { label: 'Unlocked',  tone: 'info' },
    held:     { label: 'Held open', tone: 'warn' },
    forced:   { label: 'Forced',    tone: 'alert' },
    offline:  { label: 'Offline',   tone: 'muted' },
    secured:  { label: 'Secured',   tone: 'alert' }
};

export const scheduleFor = (door, schedules) => schedules.find(s => s.doors.includes(door.id)) || null;

/* The open and close hours in force for a schedule today. */
export const hoursOf = (schedule, holiday) => (holiday ? schedule.holiday : [schedule.open, schedule.close]);

/* Whether the clock is inside a schedule's hours today. A close at or
   before the open runs past midnight. */
export function inWindow(schedule, holiday, at = new Date()) {
    const [open, close] = hoursOf(schedule, holiday);
    const h = at.getHours() + at.getMinutes() / 60;
    return close > open ? h >= open && h < close : h >= open || h < close;
}

/* True while a free-passage schedule is holding a door unlocked. */
export function freePassageNow(door, schedules, holiday) {
    const schedule = scheduleFor(door, schedules);
    return !!(schedule && schedule.kind === 'unlock' && schedule.enabled && inWindow(schedule, holiday));
}

/* What a door is right now. `open` is a momentary unlock still running. */
export function doorState(door, { lockdown, schedules, holiday }) {
    if (door.status === 'offline') return 'offline';
    if (lockdown) return 'secured';
    if (door.status === 'forced') return 'forced';
    if (door.status === 'held') return 'held';
    if (door.pulseUntil > Date.now()) return 'open';
    if (door.override === 'unlocked') return 'unlocked';
    if (door.override === 'locked') return 'locked';
    return freePassageNow(door, schedules, holiday) ? 'unlocked' : 'locked';
}

/* 7 -> "7:00 AM", 24 -> "12:00 AM". */
export function formatHour(h) {
    const hour = ((h % 24) + 24) % 24;
    const twelve = hour % 12 === 0 ? 12 : hour % 12;
    return `${twelve}:00 ${hour < 12 ? 'AM' : 'PM'}`;
}

export const hoursText = (schedule, holiday) => {
    const [open, close] = hoursOf(schedule, holiday);
    return `${formatHour(open)} to ${formatHour(close)}`;
};

/* The schedule line a door card prints. */
export function scheduleText(door, schedules, holiday) {
    const schedule = scheduleFor(door, schedules);
    const tag = holiday ? ' (holiday)' : '';
    if (!schedule) return 'Card access, 24 hours';
    if (!schedule.enabled) return schedule.kind === 'unlock' ? 'Card access only, free passage paused' : 'Closed, schedule off';
    if (schedule.kind === 'unlock') {
        const [open, close] = hoursOf(schedule, holiday);
        return inWindow(schedule, holiday)
            ? `Free passage until ${formatHour(close)}${tag}`
            : `Card access now, free passage from ${formatHour(open)}${tag}`;
    }
    return `${schedule.who} ${hoursText(schedule, holiday)}${tag}`;
}

/* The same line in a resident's words. */
export function residentHours(door, schedules, holiday) {
    const schedule = scheduleFor(door, schedules);
    if (!schedule || schedule.kind === 'unlock') return 'Open 24 hours';
    if (!schedule.enabled) return 'Closed by management';
    return `Open ${hoursText(schedule, holiday)}${holiday ? ', holiday hours' : ''}`;
}

/* A resident may use a door if it is theirs, open to them, and not shut
   for any reason. Returns why not, or null. */
export function residentBlock(door, { lockdown, schedules, credential }) {
    if (lockdown) return 'Lockdown in effect';
    if (!credential || credential.status !== 'Active') return 'Mobile key suspended';
    if (!credential.groups.includes(door.group)) return 'Not in your access groups';
    if (door.status === 'offline') return 'Reader offline';
    const schedule = scheduleFor(door, schedules);
    if (schedule && schedule.kind === 'hours' && !schedule.enabled) return 'Closed by management';
    return null;
}

/* "Fob 4410", "Mobile key (iPhone)". */
export function credentialLabel(c) {
    if (c.medium === 'Mobile key') return `Mobile key (${c.number})`;
    if (c.medium === 'PIN') return 'PIN';
    return `Fob ${c.number}`;
}

/* How a credential is written in the event feed. */
export const viaOf = c => (c.medium === 'Mobile key' ? 'Mobile key' : `Fob ${c.number}`);

export const holderOf = c => (c.type === 'Resident' ? `Unit ${c.unit}` : c.unit);

export const CREDENTIAL_TONE = { Active: 'ok', Suspended: 'warn', Lost: 'alert', Expired: 'muted' };
export const CODE_TONE = { Active: 'ok', Used: 'muted', Expired: 'muted', Revoked: 'alert' };

/* 34000 -> "0:34", 754000 -> "12:34", past an hour "1:02:10". */
export function elapsed(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

export const timeOf = (ts, seconds = false) => new Date(ts).toLocaleTimeString([], {
    hour: 'numeric', minute: '2-digit', ...(seconds ? { second: '2-digit' } : null)
});

/* "Today, 3:41 PM", "Yesterday, 8:12 PM", "Mon, Sep 28, 3:41 PM". */
export function whenOf(ts) {
    const d = new Date(ts);
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const time = timeOf(ts);
    if (ts >= start) return `Today, ${time}`;
    if (ts >= start - 86400000) return `Yesterday, ${time}`;
    return `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}, ${time}`;
}

/* A code is Expired once its day has passed, whatever it was stored as. */
export function codeStatus(code) {
    if (code.status !== 'Active') return code.status;
    const today = new Date();
    const pad = n => String(n).padStart(2, '0');
    const iso = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    return code.dateValue && code.dateValue < iso ? 'Expired' : 'Active';
}

/* A six-digit PIN that is not already in use. */
export function newCode(taken = []) {
    let code;
    do { code = String(Math.floor(100000 + Math.random() * 900000)); } while (taken.includes(code));
    return code;
}

/* A four-digit fob number in the resident range, not already issued. */
export function newFob(taken = [], prefix = '4') {
    let n;
    do { n = `${prefix}${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`; } while (taken.includes(n));
    return n;
}

export const spaced = code => `${code.slice(0, 3)} ${code.slice(3)}`;
